import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './Header.module.css';
import Logo from '../Logo/Logo';
import useTranslation from '../../hooks/useTranslation';
import LanguageToggle from '../LanguageToggle/LanguageToggle';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import { useTheme } from '../../contexts/ThemeContext';

const Header: React.FC = () => {
    const { t } = useTranslation();
    const { theme } = useTheme();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();
    const mobileMenuRef = useRef<HTMLDivElement>(null);
    const menuButtonRef = useRef<HTMLButtonElement>(null);

    const closeMobileMenu = useCallback(() => {
        setIsMobileMenuOpen(false);
        menuButtonRef.current?.focus();
    }, []);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(prev => !prev);
    };

    // Close menu on Escape key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isMobileMenuOpen) {
                closeMobileMenu();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isMobileMenuOpen, closeMobileMenu]);

    // Close mobile menu on route change
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    const isActive = (path: string) => {
        return location.pathname === path;
    };

    // data-site-header distinguishes the site chrome from the <header> elements
    // used inside sections and cards. Theme rules must target it explicitly —
    // a bare `header` selector leaks onto every section title.
    return (
        <header className={styles.header} data-site-header>
            <div className={styles.headerContent}>
                <Link to="/" className={styles.logoContainer}>
                    <Logo size="medium" variant={theme === 'light' ? 'light' : 'dark'} />
                </Link>
                
                {/* Desktop Navigation */}
                <nav className={styles.nav}>
                    <ul className={styles.navList}>
                        <li className={styles.navItem}>
                            <Link 
                                to="/" 
                                className={`${styles.navLink} ${isActive('/') ? styles.active : ''}`}
                                onClick={closeMobileMenu}
                            >
                                {t('navigation.home')}
                            </Link>
                        </li>
                        <li className={styles.navItem}>
                            <Link 
                                to="/about" 
                                className={`${styles.navLink} ${isActive('/about') ? styles.active : ''}`}
                                onClick={closeMobileMenu}
                            >
                                {t('navigation.about')}
                            </Link>
                        </li>
                        <li className={styles.navItem}>
                            <Link 
                                to="/experience" 
                                className={`${styles.navLink} ${isActive('/experience') ? styles.active : ''}`}
                                onClick={closeMobileMenu}
                            >
                                {t('navigation.experience')}
                            </Link>
                        </li>
                        <li className={styles.navItem}>
                            <Link 
                                to="/skills" 
                                className={`${styles.navLink} ${isActive('/skills') ? styles.active : ''}`}
                                onClick={closeMobileMenu}
                            >
                                {t('navigation.skills')}
                            </Link>
                        </li>
                        <li className={styles.navItem}>
                            <Link 
                                to="/contact" 
                                className={`${styles.navLink} ${isActive('/contact') ? styles.active : ''}`}
                                onClick={closeMobileMenu}
                            >
                                {t('navigation.contact')}
                            </Link>
                        </li>
                    </ul>
                </nav>

                <div className={styles.headerRight}>
                    <ThemeToggle />
                    <LanguageToggle />
                    
                    {/* Mobile Menu Button */}
                    <button 
                        ref={menuButtonRef}
                        className={`${styles.mobileMenuButton} ${isMobileMenuOpen ? styles.open : ''}`}
                        onClick={toggleMobileMenu}
                        aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={isMobileMenuOpen}
                        aria-controls="mobile-menu"
                    >
                        <span className={styles.hamburger}></span>
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            <div 
                id="mobile-menu"
                ref={mobileMenuRef}
                className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.open : ''}`}
                role="navigation"
                aria-label="Mobile navigation"
            >
                <nav>
                    <ul className={styles.mobileNavList}>
                        <li>
                            <Link 
                                to="/" 
                                className={`${styles.mobileNavLink} ${isActive('/') ? styles.active : ''}`}
                                onClick={closeMobileMenu}
                            >
                                {t('navigation.home')}
                            </Link>
                        </li>
                        <li>
                            <Link 
                                to="/about" 
                                className={`${styles.mobileNavLink} ${isActive('/about') ? styles.active : ''}`}
                                onClick={closeMobileMenu}
                            >
                                {t('navigation.about')}
                            </Link>
                        </li>
                        <li>
                            <Link 
                                to="/experience" 
                                className={`${styles.mobileNavLink} ${isActive('/experience') ? styles.active : ''}`}
                                onClick={closeMobileMenu}
                            >
                                {t('navigation.experience')}
                            </Link>
                        </li>
                        <li>
                            <Link 
                                to="/skills" 
                                className={`${styles.mobileNavLink} ${isActive('/skills') ? styles.active : ''}`}
                                onClick={closeMobileMenu}
                            >
                                {t('navigation.skills')}
                            </Link>
                        </li>
                        <li>
                            <Link 
                                to="/contact" 
                                className={`${styles.mobileNavLink} ${isActive('/contact') ? styles.active : ''}`}
                                onClick={closeMobileMenu}
                            >
                                {t('navigation.contact')}
                            </Link>
                        </li>
                    </ul>
                </nav>
            </div>
        </header>
    );
};

export default Header;